import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import AddServerModal from './AddServerModal';
import { api } from './api';
import { Server, UptimePoint, WSMessage } from './types';

function UptimeBar({ serverId }: { serverId: number }) {
    const [data, setData] = useState<UptimePoint[]>([]);

    useEffect(() => {
        api.get<UptimePoint[]>(`/servers/${serverId}/uptime`)
            .then(setData)
            .catch(() => {});
    }, [serverId]);

    if (!data.length) return <div className="uptime-bar-empty">–</div>;

    return (
        <div className="uptime-bar" title="Uptime за 24 часа">
            {data.map((h, i) => {
                const cls =
                    h.pings === 0    ? 'seg-nodata' :
                    h.uptime === 100 ? 'seg-up' :
                    h.uptime === 0   ? 'seg-down' : 'seg-partial';
                const label = h.pings === 0
                    ? 'Нет данных'
                    : `${h.uptime}% (${h.pings} пингов)`;
                return <div key={i} className={`uptime-seg ${cls}`} title={label} />;
            })}
        </div>
    );
}

function StatusBadge({ status }: { status: Server['status'] }) {
    const map = {
        up:      { label: 'Online',  cls: 'badge-up' },
        down:    { label: 'Offline', cls: 'badge-down' },
        unknown: { label: '...',     cls: 'badge-unknown' },
    } as const;
    const { label, cls } = map[status] ?? map.unknown;
    return <span className={`status-badge ${cls}`}>{label}</span>;
}

function fmtLatency(ms: number | null): string {
    if (ms === null) return '—';
    return ms < 1 ? '<1 мс' : `${Math.round(ms)} мс`;
}

function fmtTime(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso + 'Z');
    return d.toLocaleTimeString('ru-RU', {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
}

export default function Servers() {
    const [servers, setServers]     = useState<Server[]>([]);
    const [loading, setLoading]     = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [filterTag, setFilterTag] = useState<string>('');
    const [wsStatus, setWsStatus]   = useState<'connecting' | 'ok' | 'error'>('connecting');
    const wsRef = useRef<WebSocket | null>(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        try {
            const list = await api.get<Server[]>('/servers');
            setServers(list);
        } catch { /* авторизация обрабатывается в api.ts */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const url   = `${proto}//${window.location.host}/api/ws`;

        const connect = () => {
            const ws = new WebSocket(url);
            wsRef.current = ws;

            ws.onopen  = () => setWsStatus('ok');
            ws.onerror = () => setWsStatus('error');
            ws.onclose = () => {
                setWsStatus('error');
                setTimeout(connect, 5000);
            };

            ws.onmessage = (e) => {
                try {
                    const msg: WSMessage = JSON.parse(e.data);
                    if (msg.type === 'status_update') {
                        setServers(prev => prev.map(s =>
                            s.id === msg.server_id
                                ? {
                                    ...s,
                                    status: msg.status,
                                    last_latency: msg.latency,
                                    last_seen: msg.timestamp ?? s.last_seen,
                                  }
                                : s
                        ));
                    }
                } catch { /* некорректное сообщение — игнорируем */ }
            };
        };

        connect();
        return () => { wsRef.current?.close(); };
    }, []);

    const handleDelete = async (id: number) => {
        if (!window.confirm('Удалить сервер? История пингов будет удалена.')) return;
        try {
            await api.del(`/servers/${id}`);
            setServers(prev => prev.filter(s => s.id !== id));
        } catch (err: any) { alert(err.message); }
    };

    const handleForcePing = async (id: number) => {
        try {
            const updated = await api.post<Server>(`/servers/${id}/ping`, {});
            setServers(prev => prev.map(s => s.id === id ? updated : s));
        } catch { /* тихо игнорируем */ }
    };

    /* ── Теги и фильтрация ── */
    const allTags = Array.from(
        new Set(
            servers.flatMap(s =>
                s.tags ? s.tags.split(',').map(t => t.trim()).filter(Boolean) : []
            )
        )
    ).sort();

    const filtered = filterTag
        ? servers.filter(s => s.tags?.toLowerCase().includes(filterTag.toLowerCase()))
        : servers;

    const online  = filtered.filter(s => s.status === 'up').length;
    const offline = filtered.filter(s => s.status === 'down').length;

    return (
        <div className="app-layout">
            <Sidebar />

            <main className="main-content">
                <header className="page-header">
                    <div>
                        <h1>Серверы</h1>
                        <div className="page-subtitle">
                            {filtered.length > 0 && (
                                <>
                                    <span className="counter-up">🟢 {online} онлайн</span>
                                    {offline > 0 &&
                                        <span className="counter-down">🔴 {offline} офлайн</span>}
                                </>
                            )}
                        </div>
                    </div>
                    <div className="header-actions">
                        <span className={`ws-indicator ${wsStatus}`}>
                            {wsStatus === 'ok'
                                ? '● live'
                                : wsStatus === 'connecting'
                                ? '● подключение'
                                : '● нет связи'}
                        </span>

                        {allTags.length > 0 && (
                            <select
                                className="tag-filter"
                                value={filterTag}
                                onChange={e => setFilterTag(e.target.value)}
                            >
                                <option value="">Все теги</option>
                                {allTags.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        )}

                        <button
                            className="btn-secondary"
                            onClick={() => window.open('/api/export/servers', '_blank')}
                        >
                            📥 CSV
                        </button>

                        <button
                            className="btn-primary"
                            style={{ maxWidth: 180 }}
                            onClick={() => setShowModal(true)}
                        >
                            + Добавить сервер
                        </button>
                    </div>
                </header>

                {loading ? (
                    <div className="loading-screen">Загрузка...</div>
                ) : servers.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📡</div>
                        <h2>Серверов пока нет</h2>
                        <p>Добавьте первый сервер для начала мониторинга</p>
                        <button
                            className="btn-primary"
                            style={{ maxWidth: 220 }}
                            onClick={() => setShowModal(true)}
                        >
                            + Добавить первый сервер
                        </button>
                    </div>
                ) : (
                    <div className="table-wrap">
                        <table className="servers-table">
                            <thead>
                                <tr>
                                    <th>Название / Хост</th>
                                    <th>Статус</th>
                                    <th>Задержка</th>
                                    <th>Последний ответ</th>
                                    <th>Uptime 24ч</th>
                                    <th>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(s => (
                                    <tr key={s.id} className={s.status === 'down' ? 'row-down' : ''}>
                                        <td
                                            className="td-name-clickable"
                                            onClick={() => navigate(`/servers/${s.id}`)}
                                        >
                                            <div className="server-name">{s.name}</div>
                                            <div className="server-host">{s.host}</div>
                                            {s.description &&
                                                <div className="server-desc">{s.description}</div>}
                                            {s.tags && (
                                                <div className="server-tags">
                                                    {s.tags.split(',').map(t => t.trim()).filter(Boolean).map(t => (
                                                        <span key={t} className="tag-pill">{t}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                        <td><StatusBadge status={s.status} /></td>
                                        <td className="td-num">{fmtLatency(s.last_latency)}</td>
                                        <td className="td-time">{fmtTime(s.last_seen)}</td>
                                        <td><UptimeBar serverId={s.id} /></td>
                                        <td>
                                            <div className="row-actions">
                                                <button
                                                    className="btn-icon"
                                                    title="Пингануть сейчас"
                                                    onClick={() => handleForcePing(s.id)}
                                                >
                                                    🔄
                                                </button>
                                                <button
                                                    className="btn-icon btn-icon-danger"
                                                    title="Удалить"
                                                    onClick={() => handleDelete(s.id)}
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>

            {showModal && (
                <AddServerModal
                    onClose={() => setShowModal(false)}
                    onAdded={srv => setServers(prev => [...prev, srv])}
                />
            )}
        </div>
    );
}
